import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {BrowserSessionService} from '@webmate-web-core/shared/services/browsersession.service';
import {ArtifactId, ExpeditionId, FindingId, ProjectId, TestRunId} from '@webmate-web-core/shared/types/ids';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ConsoleInfo, WORKBENCH_VIEW_CONFIG, WorkbenchViewConfig} from '../../../shared/types';
import {WorkbenchViewComponent} from '../../../shared/components/viewcontainers/workbench-view.component';
import {FactRequest, observationTargetEquals, Spec} from '@webmate-web-core/shared/types/testmgmt';
import {BrowserSessionInfo} from '@webmate-web-core/shared/types/browsersession';
import {WorkbenchLayoutManager} from '../../../shared/services/workbench-layout-manager.service';
import {filter, finalize, map, mergeMap, take, tap, timeout} from 'rxjs/operators';
import {BehaviorSubject, of, Subscription} from 'rxjs';
import {faExclamationCircle, faInfoSquare, faSparkles, faTimesCircle} from '@fortawesome/pro-solid-svg-icons';
import {WorkbenchExpeditionService} from '../../../shared/services/workbench-expedition.service';
import {WellKnownArtifactTypes} from '@webmate-web-core/shared/types/artifact';
import {faInfoCircle} from '@fortawesome/pro-light-svg-icons';
import {faEye, faEyeSlash} from '@fortawesome/free-solid-svg-icons';
import {WorkbenchContextService} from '../../../shared/services/workbench-context.service';
import {WorkbenchKeyValStore} from '../../../shared/services/workbench-store.service';
import {WorkbenchTestService} from '../../../shared/services/workbench-test.service';
import {AuditInfo} from '../../../shared/services/audit-session';
import {IssueSeverity} from '../../../../webmate/shared/console/types/shared';
import {WorkbenchCommandRegistry} from '../../../shared/services/workbench-command-registry.service';

export interface IncludedObservations {
    nodeId?: string;
    nodeName?: string;
    tabIndex?: string;
    fullXPath?: string;
    cssSelector?: string;
    text?: string;
    index?: number;
}
export interface TabSimulatedObservations {
    cssSelector?: string;
    tagName?: string;
    text?: string;
    className?: string,
    tabIndex?: string;
    index?:number;
    focusIndicator?: string;
}

export interface ViolationDetail {
    nodeName?: string;
    text?: string;
    tabIndex?: string;
    cssSelector?: string;
    focusIndicator?: string;
    violation?: string;
    description?: string;
    wcagCriterion?: string;
    wcagLevel?: string;
    impact?: string;
    status?: string;
    screenshotId?: string;
}

export interface ViolationEntry {
    violationType: string;
    violationString: string;
    details: ViolationDetail[];
}

export interface CompliantEntry {
    compliantType: string;
    compliantString: string;
    details: ViolationDetail[];
}

interface ProgressState {
    msg: string;
    time: string;
}
@UntilDestroy()
@Component({
  templateUrl: './tab-order-analysis.component.html',
  styleUrls: ['./tab-order-analysis.component.less'],
})
export class TabOrderAnalysisComponent extends WorkbenchViewComponent implements OnInit, OnDestroy {
    id: string;
    projectId: ProjectId;
    userchoice: boolean = false;
    clicked: boolean = false;
    activeExpeditionId?: ExpeditionId;
    previousIndex: number | null = null;
    activeConsole: ConsoleInfo | null = null
    analyzing: boolean = false;
    errorMsg = ""
    showResultsPending: boolean = false;
    resultsPublished: boolean = false;
    progressStates = {
        1: { msg: "Initializing Analysis", time: "1 min left..." },
        2: { msg: "Processing Tab Order", time: "30 seconds left..." },
        3: { msg: "Finished", time: '' }
    };
    counter = 0;
    userToggle: boolean = false;
    progressState: ProgressState = this.progressStates[1];
    includedObservationsList : IncludedObservations[] = [];
    tabObservationsList: TabSimulatedObservations[] = [];
    violationsList: ViolationEntry[] = [];
    compliantList: CompliantEntry[] = [];
    stateOptions: any[] = [{ label: 'Tabbed', value: 'tabbed', tooltip: "Tab-Order as User's Experience" },{ label: 'DOM', value: 'dom', tooltip: 'Tab-Order as DOM Inspection' }];
    tabView: string = 'tabbed';
    showConnectorLines: boolean = true;


    activeExpedition?: BrowserSessionInfo;
    forceHaltAnalysis = new BehaviorSubject(false);
    auditInfo?: AuditInfo;
    artifactId?: ArtifactId;

    addedToFinding?: boolean = false;
    lastFindingId: FindingId;
    activeTestRun?: TestRunId;


    constructor(@Inject(WORKBENCH_VIEW_CONFIG) public config: WorkbenchViewConfig<any>,
                private browserSessionService: BrowserSessionService,
                private store: WorkbenchKeyValStore,
                layoutManager: WorkbenchLayoutManager,
                private workbenchExpeditionService: WorkbenchExpeditionService,
                private workbenchTestService: WorkbenchTestService,
                private workbenchContextService: WorkbenchContextService,
                private readonly workbenchCommandRegistry: WorkbenchCommandRegistry,
    ) {
        super(layoutManager, config);
    }

    ngOnInit() {
        this.projectId = this.workbenchContextService.getProjectId();

        this.workbenchContextService.subscribeWorkbenchContext$()
            .subscribe(((context) => {
            if (this.activeExpeditionId != context.expeditionContext?.activeExpedition?.id) {
                this.activeExpeditionId = context.expeditionContext?.activeExpedition?.id;
                //TODO
            }
            this.activeTestRun = context?.testRunContext?.activeTestRun;
            this.activeConsole = context.deviceContext?.activeConsole || null;
        }));

        this.store.subscribeToValue('TODO.shownTabOrder').subscribe((nlpArtifact: ArtifactId) => {
            if (this.analyzing){
                this.forceHaltAnalysis.next(true)
            }
        });
    }

    ngOnDestroy() {
    }

    toggleUserChoice(userChoice: any) {
        this.counter = 0;
        this.removeHighlights();
        if (userChoice == "dom") {
            this.userchoice = true;
            this.highlight(this.includedObservationsList);
            this.userToggle = false;
            this.onActiveIndexChange(0, true);
        } else {
            this.userchoice = false;
            this.highlight(this.tabObservationsList);
            this.userToggle = false;
            this.onActiveIndexChange(0, true);
        }
    }

    resetAnalysisState(){
        this.analyzing = true;
        this.showResultsPending = true;
        this.errorMsg = "";
        this.resultsPublished = false;
        this.removeHighlights();
        this.userToggle = false;
        this.tabView = 'tabbed';
        this.userchoice = false;
        this.previousIndex = null;
        this.showConnectorLines = true;
    }

    analyzeLink() {
        this.progressState = this.progressStates[1];
        this.resetAnalysisState();
        this.highlight('.');
        this.counter = 0;
        setTimeout(() => {
            if (!this.activeExpeditionId) {
                this.errorMsg = 'No active browser session found.';
                return;
            }
            this.workbenchContextService.lockActiveExpedition();
            const factRequest: FactRequest = {
                factType: { artifactType: WellKnownArtifactTypes.TabOrderAnalysis },
            }
            let analysisSubscription: Subscription | null = null;
            analysisSubscription = this.browserSessionService
                .analyzeState(this.activeExpeditionId, [factRequest], true)
                .pipe(
                    take(1),
                    mergeMap((stateId) => {
                        this.progressState = this.progressStates[2];
                        return this.browserSessionService.getState$(stateId).pipe(
                            map(state => {
                                return state.associatedArtifacts.find(
                                    artifact => artifact!.artifactType === WellKnownArtifactTypes.TabOrderAnalysis
                                );
                            }),
                            filter(a11yArt => {
                                const exists = !!a11yArt;
                                return exists;
                            }),
                            take(1),
                            timeout(300000),
                        );
                    }),
                    untilDestroyed(this),
                    finalize(() => this.workbenchContextService.unlockActiveExpedition())
                )
                .subscribe({
                    next: (nlpArtifact) => {
                        this.progressState = this.progressStates[3];
                        if (nlpArtifact) {
                            this.processArtifact(nlpArtifact);
                        } else {
                            this.errorMsg = "Tab Order Analysis artifact not found.";
                        }
                        this.showResultsPending = false;
                    },
                    error: (err) => {
                        this.errorMsg = "Tab Order Analysis Failed: Please try again later."
                        this.showResultsPending = false;
                        this.analyzing = false;
                    }
                });
        },350);
    }

    processArtifact(nlpArtifact: any){
        this.analyzing = false;
        this.resultsPublished = true;
        this.tabView = 'tabbed';
        this.userchoice = false;
        this.previousIndex = null;
        this.includedObservationsList = Object.values(nlpArtifact.data.analysis.includedObs)
        this.tabObservationsList = Object.values(nlpArtifact.data.analysis.tabSimulatedObs)
        this.violationsList = Object.entries(nlpArtifact.data.analysis.violationObs).map(
            ([violationType, violationData]) => ({
                violationType,
                violationString: (violationData as any).violationString,
                details: (violationData as any).details as ViolationDetail[]
            })
        );
        this.compliantList = Object.entries(nlpArtifact.data.analysis.compliantObs ?? {}).map(
            ([compliantType, compliantData]) => ({
                compliantType,
                compliantString: (compliantData as any).compliantString,
                details: (compliantData as any).details as ViolationDetail[]
            })
        );
        this.highlight(this.tabObservationsList);
    }

    highlight(list: any) {
        this.counter = -1;
        Object.values(list).map((observation: any) => {
            this.counter++;
            this.workbenchExpeditionService.sendCDPCommand('Runtime.evaluate', {
                expression: `window.WebmateInfusion.highlightElements('${observation.cssSelector}', {
                'scrollFirstIntoView': true,
                'clearHudItems': false
         }, ${this.counter}, false);`
            }).subscribe({
                next: res => {},
                error: err => {console.error('Error while highlighting element', err);}
            });
        })
    }

    toggleConnectorLines() {
        this.showConnectorLines = !this.showConnectorLines;
        this.workbenchExpeditionService.sendCDPCommand('Runtime.evaluate', {
            expression: `window.WebmateInfusion.setConnectors(${this.showConnectorLines});`
        }).subscribe({
            next: res => {},
            error: err => {console.error('Error while toggling tab-order connector lines', err);}
        });
    }

    onActiveIndexChange(index: number, checkToggle= false) {
        if(!checkToggle) {
            if (index !== this.previousIndex) {
                if (this.previousIndex !== null) {
                    this.changeItemHighlightColor(this.previousIndex, false);
                }
                if (index !== null) {
                    this.changeItemHighlightColor(index, true);
                }
                this.previousIndex = index;
            }
        }
        else {
            this.previousIndex = null;
        }
    }

    changeItemHighlightColor(target: any, selected: boolean) {
        this.workbenchExpeditionService.sendCDPCommand('Runtime.evaluate', {
            expression: `window.WebmateInfusion.changeHighlightColor('${target}', ${selected});`
        }).subscribe({
            next: res => {
                // Handle the response if needed
            },
            error: err => {
                console.error('Error while removing highlight elements', err);
            }
        });
    }

    removeHighlights() {
        this.workbenchExpeditionService.sendCDPCommand('Runtime.evaluate', {
            expression: `window.WebmateInfusion.clearHudItems();`
        }).subscribe({
            next: res => {
                // Handle the response if needed
            },
            error: err => {
                console.error('Error while removing highlight elements', err);
            }
        });
    }

    addNewFinding(violationType: string, observation: ViolationDetail) {
        const title = `${violationType}: ${observation.nodeName}: ${observation.text}`;
        const text = [];
        if (observation.tabIndex) {
            text.push(`TabIndex: ${observation.tabIndex}`);
        }
        text.push("Focus Indicator: Cannot Tell");
        if (observation.cssSelector) {
            text.push(`Selector: ${observation.cssSelector}`);
        }
        const findingText = text.join('<br>');
        return this.workbenchTestService
            .createNewManualFinding(title, IssueSeverity.Error)
            .pipe(
                mergeMap(findingId =>
                    this.workbenchTestService.addTextToFinding(findingId, findingText).pipe(
                        tap(() => {}),
                        map(() => findingId)
                    ))).subscribe(findingId => {
                this.lastFindingId = findingId;
                this.addedToFinding = true;
                setTimeout(() => (this.addedToFinding = false), 5000);
            });
    }


    openFinding() {
        if (this.lastFindingId && this.addedToFinding) {
            this.workbenchCommandRegistry.executeCommand("testmgmt:openViewContainerWithFinding", {findingId: this.lastFindingId});
        }
    }


    protected readonly faEye = faEye;
    protected readonly faEyeSlash = faEyeSlash;
    protected readonly faSparkles = faSparkles;
    protected readonly faInfoSquare = faInfoSquare;
    protected readonly observationTargetEquals = observationTargetEquals;
    protected readonly faInfoCircle = faInfoCircle;
    protected readonly faTimesCircle = faTimesCircle;
    protected readonly faExclamationCircle = faExclamationCircle;
}
